import { createContext, useContext } from 'react';
import React from 'react';

/**
 * React Context for managing module state and actions throughout the component tree.
 * 
 * Provides access to module state, dispatch functions, UI state, and navigation controls
 * for all components within a module. This context is the foundation of the module
 * system's state management architecture.
 * 
 * @typedef {Object} ModuleContextValue
 * @property {Object} moduleState - Complete module data structure with sections, slides, and components
 * @property {Object} moduleState.title - Module title and metadata
 * @property {Array<Object>} moduleState.sections - Array of module sections containing slides
 * @property {string} moduleState.internalNotes - Educator notes and comments
 * @property {Function} dispatch - Reducer dispatch function for state mutations
 * @property {Object} moduleUI - UI state and navigation controls
 * @property {number} moduleUI.currentSlideIndex - Currently active slide index
 * @property {number} moduleUI.currentSectionIndex - Currently active section index
 * @property {Function} moduleUI.goToNextSlide - Navigate to next slide
 * @property {Function} moduleUI.goToPreviousSlide - Navigate to previous slide
 * @property {Function} moduleUI.goToSlide - Navigate to specific slide by index
 * @property {boolean} hasModuleBeenEdited - Flag indicating if module has unsaved changes
 * @property {string} mode - Current module mode ("STUDENT", "EDITOR", "SUBMISSION", "PREVIEW")
 * @property {string|null} moduleId - Database ID of the current module
 * @property {string|null} programId - Database ID of the associated program
 * @property {string} status - Module completion status ("in_progress", "completed")
 * @property {Function} loadUserModuleRecord - Function to reload module data from database
 * 
 * @context
 */
export const ModuleContext = createContext();

/**
 * Hook to access module context with error boundary protection.
 * 
 * Provides type-safe access to module state and actions. Must be used within
 * a ModuleProvider component or will throw a descriptive error.
 * 
 * @returns {ModuleContextValue} Complete module context object with state and actions
 * 
 * @throws {Error} When used outside of ModuleContext.Provider
 * 
 * @example
 * // Basic usage in a component
 * function ModuleComponent() {
 *   const { moduleState, dispatch, moduleUI } = useModuleContext();
 *   
 *   const handleAddSlide = () => {
 *     dispatch({
 *       type: "ADD_SLIDE",
 *       sectionIndex: moduleUI.currentSectionIndex
 *     });
 *   };
 *   
 *   return (
 *     <div>
 *       <h1>{moduleState.title}</h1>
 *       <button onClick={handleAddSlide}>Add Slide</button>
 *     </div>
 *   );
 * }
 * 
 * @example
 * // Accessing navigation controls
 * function NavigationControls() {
 *   const { moduleUI: { goToNextSlide, goToPreviousSlide, currentSlideIndex } } = useModuleContext();
 *   
 *   return (
 *     <div>
 *       <button onClick={goToPreviousSlide} disabled={currentSlideIndex === 0}>
 *         Previous
 *       </button>
 *       <button onClick={goToNextSlide}>
 *         Next
 *       </button>
 *     </div>
 *   );
 * }
 * 
 * @example
 * // Recording student responses
 * function QuestionComponent({ componentIndex, slideIndex, sectionIndex }) {
 *   const { dispatch } = useModuleContext();
 *   
 *   const handleResponse = (answer) => {
 *     dispatch({
 *       type: "RECORD_RESPONSE",
 *       slideIndex,
 *       componentIndex,
 *       sectionIndex,
 *       payload: answer
 *     });
 *   };
 *   
 *   return <input onChange={(e) => handleResponse(e.target.value)} />;
 * }
 * 
 * @since 1.0.0
 * @memberof CurriculumContext
 */
export const useModuleContext = () => {
  const context = useContext(ModuleContext);
  if (!context) {
    throw new Error('useModuleContext must be used within an ModuleContext.Provider');
  }
  return context;
};